import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { fmtINR, todayISO } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, ShieldCheck, ShieldX, Truck, Printer } from "lucide-react";
import RoleGuide from "@/components/RoleGuide";

export default function DebtorLedger() {
  const { user } = useAuth();
  const [ledger, setLedger] = useState([]);
  const [payments, setPayments] = useState([]);
  const [debtors, setDebtors] = useState([]);
  const [form, setForm] = useState({
    date: todayISO(), debtor_id: "", amount: 0,
    mode: "NEFT", note: "",
    reference_no: "", cheque_no: "", tds_amount: 0, settled_bills_text: "",
    bank_statement_file_id: null, bank_statement_name: "",
    receipt_file_id: null, receipt_name: "",
  });
  const [bankStmt, setBankStmt] = useState({ pasted: "", suggestions: [] });
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [uploadingBank, setUploadingBank] = useState(false);
  const [proposal, setProposal] = useState({ name: "", contact_person: "", contact_phone: "", credit_limit: 0, credit_period_days: 30, has_deposit: false, deposit_amount: 0, deposit_date: "", invoice_file_id: null, invoice_name: "", notes: "" });
  const [showPropose, setShowPropose] = useState(false);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  // per-vehicle statement state
  const firstOfMonth = todayISO().slice(0, 8) + "01";
  const [stmt, setStmt] = useState({ debtor_id: "", from_date: firstOfMonth, to_date: todayISO() });
  const [stmtData, setStmtData] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    const [l, p, d] = await Promise.all([
      api.get("/manager/debtor_ledger"),
      api.get("/manager/debtor_payments"),
      api.get("/masters/debtors"),
    ]);
    setLedger(l.data);
    setPayments(p.data);
    setDebtors(d.data);
  };
  useEffect(() => { load(); }, []);

  const pending = debtors.filter((d) => d.status === "Pending_Approval");
  const isOwnerLike = ["owner", "admin"].includes(user?.role);

  // Memoise active-debtor list — used in two <select>s + several list renders.
  const activeDebtors = useMemo(
    () => debtors.filter((d) => d.status === "Active"),
    [debtors],
  );
  const PAY_MODES = useMemo(
    () => ["NEFT", "RTGS", "IMPS", "UPI", "Cheque", "Cash", "IOCL adjustment", "Other"],
    [],
  );

  const save = async () => {
    try {
      const payload = {
        ...form,
        settled_bills: (form.settled_bills_text || "").split(",").map((s) => s.trim()).filter(Boolean),
      };
      delete payload.settled_bills_text;
      delete payload.bank_statement_name;
      delete payload.receipt_name;
      await api.post("/manager/debtor_payments", payload);
      toast.success("Payment recorded");
      setForm({ ...form, amount: 0, reference_no: "", cheque_no: "", tds_amount: 0, settled_bills_text: "", receipt_file_id: null, receipt_name: "" });
      load();
    }
    catch (e) { toast.error(apiError(e)); }
  };

  // Parse a pasted bank-statement CSV or plain-text lines: "date, amount, narration, ref"
  const parsePasted = () => {
    const rows = [];
    for (const line of (bankStmt.pasted || "").split(/\r?\n/)) {
      const l = line.trim();
      if (!l) continue;
      const parts = l.split(/[,;\t]/).map((s) => s.trim());
      if (parts.length < 2) continue;
      const date = parts[0].replace(/\//g, "-");
      const amount = Number(parts[1].replace(/[^\d.-]/g, ""));
      if (!amount) continue;
      rows.push({ date, amount, narration: parts[2] || "", reference_no: parts[3] || "" });
    }
    return rows;
  };

  const previewBank = async () => {
    const rows = parsePasted();
    if (!rows.length) { toast.error("Paste some lines first (date, amount, narration, ref)"); return; }
    try {
      const r = await api.post("/manager/debtor_payments/bank_statement/preview", { rows, file_id: form.bank_statement_file_id });
      setBankStmt({ ...bankStmt, suggestions: r.data.suggestions });
      toast.success(`${r.data.suggestions.length} rows analysed`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const commitBank = async () => {
    const accepted = bankStmt.suggestions
      .filter((s) => s.suggested_debtor_id)
      .map((s) => ({
        date: s.row.date,
        amount: s.row.amount,
        debtor_id: s.suggested_debtor_id,
        mode: "Bank Transfer",
        reference_no: s.row.reference_no,
        narration: s.row.narration,
      }));
    if (!accepted.length) { toast.error("No matched rows to commit"); return; }
    try {
      const r = await api.post("/manager/debtor_payments/bank_statement/commit", { rows: accepted, file_id: form.bank_statement_file_id });
      toast.success(`Inserted ${r.data.inserted}, skipped ${r.data.skipped}`);
      setBankStmt({ pasted: "", suggestions: [] });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const propose = async () => {
    try { await api.post("/masters/debtors", proposal); toast.success(isOwnerLike ? "Debtor created" : "Proposal sent to owner for approval"); setShowPropose(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const decide = async (id, decision) => {
    const reason = decision === "reject" ? (prompt("Reason for rejection?") || "") : "";
    try { await api.post(`/masters/debtors/${id}/approve`, { decision, reason }); toast.success(`Debtor ${decision}d`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const loadStatement = async () => {
    if (!stmt.debtor_id) { toast.error("Select a debtor first"); return; }
    try {
      const r = await api.get("/manager/debtor_vehicle_statement", { params: stmt });
      setStmtData(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const printStatement = () => window.print();

  return (
    <div className="space-y-4" data-testid="debtor-root">
      <Toaster position="top-center" />
      <div className="flex items-end justify-between flex-wrap gap-2">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Debtor Ledger</h1>
        <Button data-testid="propose-debtor-btn" onClick={() => setShowPropose(!showPropose)} className="bg-slate-900 hover:bg-slate-800 text-white">
          <UserPlus size={14} className="mr-1" /> {showPropose ? "Cancel" : "Propose new credit account"}
        </Button>
      </div>

      <RoleGuide guides={{
        cashier: { freq: "on receipt", title: "Record payments from B2B clients", text: "When Afcons/Bhoir pays their bill, add the payment row with mode + amount. Outstanding auto-updates." },
        manager: { freq: "monthly", title: "Propose new credit accounts", text: "Tap the button above to propose a new B2B customer (name, contact, credit period, deposit). Owner approval is needed before the account can be used on shifts." },
        assistant_manager: { freq: "weekly", title: "Watch outstanding + build B2B statements", text: "The per-vehicle statement card at the bottom groups all credit sales by truck — hand this to your B2B contacts every month." },
        owner: { freq: "as needed", title: "Approve credit accounts", text: "Any pending approval sits in the red panel below. Approve/reject with a reason — the system logs it." },
      }} />

      {showPropose && (
        <Card className="p-4 border-2 border-amber-500 bg-amber-50 rounded-sm" data-testid="propose-debtor-card">
          <h3 className="font-bold text-amber-900 mb-2 uppercase tracking-wider text-sm">
            {isOwnerLike ? "Create credit account (as owner)" : "Propose new credit account — needs owner approval"}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <Input placeholder="Business name *" value={proposal.name} onChange={(e) => setProposal({ ...proposal, name: e.target.value })} className="h-10 border border-slate-300" data-testid="prop-name" />
            <Input placeholder="Contact person" value={proposal.contact_person} onChange={(e) => setProposal({ ...proposal, contact_person: e.target.value })} className="h-10 border border-slate-300" data-testid="prop-contact-person" />
            <Input placeholder="Phone" value={proposal.contact_phone} onChange={(e) => setProposal({ ...proposal, contact_phone: e.target.value })} className="h-10 border border-slate-300" data-testid="prop-contact-phone" />
            <Input type="number" step="0.01" placeholder="Credit limit ₹" value={proposal.credit_limit} onChange={(e) => setProposal({ ...proposal, credit_limit: Number(e.target.value) })} className="h-10 border border-slate-300 num" data-testid="prop-credit-limit" />
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Credit period (days)</label>
              <Input type="number" step="1" placeholder="30" value={proposal.credit_period_days} onChange={(e) => setProposal({ ...proposal, credit_period_days: Number(e.target.value) })} className="h-10 border border-slate-300 num" data-testid="prop-credit-period" />
            </div>
            <label className="flex items-center gap-2 text-sm h-10"><input type="checkbox" checked={proposal.has_deposit} onChange={(e) => setProposal({ ...proposal, has_deposit: e.target.checked })} data-testid="prop-has-deposit" /> Has deposit</label>
            {proposal.has_deposit && <>
              <Input type="number" step="0.01" placeholder="Deposit amount ₹" value={proposal.deposit_amount} onChange={(e) => setProposal({ ...proposal, deposit_amount: Number(e.target.value) })} className="h-10 border border-slate-300 num" data-testid="prop-deposit-amount" />
              <Input type="date" value={proposal.deposit_date} onChange={(e) => setProposal({ ...proposal, deposit_date: e.target.value })} className="h-10 border border-slate-300" data-testid="prop-deposit-date" />
            </>}
            <div className="col-span-full md:col-span-3">
              <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">PO / signed credit agreement (optional)</label>
              <div className="flex items-center gap-2 mt-1">
                <label className="flex items-center gap-1 text-xs font-bold px-2 py-2 bg-white border-2 border-slate-300 rounded-sm cursor-pointer hover:bg-slate-50">
                  <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="prop-invoice-input"
                         onChange={async (e) => {
                           const f = e.target.files?.[0]; e.target.value = "";
                           if (!f) return;
                           setUploadingInvoice(true);
                           try {
                             const fd = new FormData(); fd.append("file", f); fd.append("note", `Debtor invoice: ${proposal.name || "(new)"}`);
                             const r = await api.post("/uploads/", fd, { headers: { "Content-Type": "multipart/form-data" } });
                             setProposal((p) => ({ ...p, invoice_file_id: r.data.file_id, invoice_name: r.data.original_name }));
                             toast.success("Invoice attached");
                           } catch (err) { toast.error(apiError(err)); }
                           finally { setUploadingInvoice(false); }
                         }} />
                  {uploadingInvoice ? "Uploading…" : (proposal.invoice_file_id ? "Replace file" : "Attach PDF / photo")}
                </label>
                {proposal.invoice_file_id && (
                  <span className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-300 px-2 py-1 rounded-sm">✓ {proposal.invoice_name}</span>
                )}
              </div>
            </div>
            <Input placeholder="Notes / follow-up plan" value={proposal.notes} onChange={(e) => setProposal({ ...proposal, notes: e.target.value })} className="h-10 border border-slate-300 col-span-full md:col-span-3" data-testid="prop-notes" />
          </div>
          <Button data-testid="submit-proposal-btn" onClick={propose} disabled={!proposal.name} className="mt-3 bg-slate-900 hover:bg-slate-800 text-white">
            {isOwnerLike ? "Create" : "Send for approval"}
          </Button>
        </Card>
      )}

      {pending.length > 0 && (
        <Card className="p-3 border-2 border-red-500 bg-red-50 rounded-sm" data-testid="pending-approvals-card">
          <h3 className="font-bold text-red-900 uppercase tracking-wider text-sm mb-2">Pending approval · {pending.length}</h3>
          <table className="dense-table w-full text-sm">
            <thead><tr><th>Name</th><th>Contact</th><th>Credit</th><th>Deposit</th><th>Requested by</th><th></th></tr></thead>
            <tbody>
              {pending.map((d) => (
                <tr key={d.debtor_id}>
                  <td className="font-bold">{d.name}</td>
                  <td className="text-xs">{d.contact_person} · {d.contact_phone}</td>
                  <td className="num">{fmtINR(d.credit_limit)}</td>
                  <td className="text-xs">{d.has_deposit ? `${fmtINR(d.deposit_amount)} on ${d.deposit_date || "—"}` : "—"}</td>
                  <td className="text-xs">{d.requested_by}</td>
                  <td>
                    {isOwnerLike ? (
                      <div className="flex gap-1">
                        <Button size="sm" onClick={() => decide(d.debtor_id, "approve")} className="text-xs h-7 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`approve-${d.debtor_id}`}><ShieldCheck size={12} /></Button>
                        <Button size="sm" variant="outline" onClick={() => decide(d.debtor_id, "reject")} className="text-xs h-7 border-red-300 text-red-700" data-testid={`reject-${d.debtor_id}`}><ShieldX size={12} /></Button>
                      </div>
                    ) : <span className="text-xs text-slate-500">Waiting on owner</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <h3 className="font-bold text-slate-800 mb-2">Record Payment Received</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Date</label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-11 mt-1" data-testid="pay-date" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Debtor *</label>
            <select value={form.debtor_id} onChange={(e) => setForm({ ...form, debtor_id: e.target.value })} className="h-11 w-full mt-1 border border-slate-300 rounded-sm px-2 bg-white" data-testid="pay-debtor">
              <option value="">— Debtor —</option>
              {activeDebtors.map((d) => <option key={d.debtor_id} value={d.debtor_id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Amount (₹) *</label>
            <Input type="number" step="0.01" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} className="h-11 num mt-1" data-testid="pay-amount" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Mode *</label>
            <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="h-11 w-full mt-1 border border-slate-300 rounded-sm px-2 bg-white" data-testid="pay-mode">
              {PAY_MODES.map((m) => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">{form.mode === "Cheque" ? "Cheque #" : "Reference # / UTR / TXN"}</label>
            <Input placeholder={form.mode === "Cheque" ? "CHQ 123456" : "UTR / txn id"} value={form.mode === "Cheque" ? form.cheque_no : form.reference_no}
                   onChange={(e) => setForm(form.mode === "Cheque" ? { ...form, cheque_no: e.target.value } : { ...form, reference_no: e.target.value })}
                   className="h-11 mt-1" data-testid="pay-reference" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">TDS (₹)</label>
            <Input type="number" step="0.01" value={form.tds_amount} onChange={(e) => setForm({ ...form, tds_amount: Number(e.target.value) })} className="h-11 num mt-1" data-testid="pay-tds" />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Settles bills (invoice #s, comma-separated)</label>
            <Input placeholder="INV-2026-001, INV-2026-004" value={form.settled_bills_text} onChange={(e) => setForm({ ...form, settled_bills_text: e.target.value })} className="h-11 mt-1" data-testid="pay-settled-bills" />
          </div>
          <div className="col-span-2 md:col-span-2">
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Money receipt (₹ receipt we hand to debtor)</label>
            <div className="flex items-center gap-2 mt-1">
              <label className="flex items-center gap-1 text-xs font-bold px-2 py-2 bg-white border-2 border-slate-300 rounded-sm cursor-pointer hover:bg-slate-50">
                <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="pay-receipt-input"
                       onChange={async (e) => {
                         const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
                         setUploadingReceipt(true);
                         try {
                           const fd = new FormData(); fd.append("file", f); fd.append("note", `Debtor money receipt ${form.debtor_id}`);
                           const r = await api.post("/uploads/", fd, { headers: { "Content-Type": "multipart/form-data" } });
                           setForm((s) => ({ ...s, receipt_file_id: r.data.file_id, receipt_name: r.data.original_name }));
                           toast.success("Receipt attached");
                         } catch (err) { toast.error(apiError(err)); } finally { setUploadingReceipt(false); }
                       }} />
                {uploadingReceipt ? "Uploading…" : (form.receipt_file_id ? "Replace" : "Attach")}
              </label>
              {form.receipt_file_id && <span className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-300 px-2 py-1 rounded-sm">✓ {form.receipt_name}</span>}
            </div>
          </div>
          <div className="col-span-2 md:col-span-4">
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Note</label>
            <Input placeholder="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="h-11 mt-1" data-testid="pay-note" />
          </div>
        </div>
        <Button data-testid="save-debt-pay-btn" className="mt-2 bg-slate-900 hover:bg-slate-800 text-white" onClick={save}>Record payment</Button>
      </Card>

      {/* Phase-3 bonus: Bank Statement importer — auto-match against debtors */}
      <Card className="p-3 border-2 border-slate-300 rounded-sm" data-testid="bank-statement-card">
        <h3 className="font-bold text-slate-800 mb-1">Bank Statement — auto-match debtor payments</h3>
        <p className="text-xs text-slate-600 mb-2">Paste bank-statement rows (one per line, comma-separated: <code>date, amount, narration, ref</code>) or upload a CSV/PDF above. The system suggests which debtor each row belongs to based on the narration.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-start">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Paste statement rows</label>
            <textarea rows={5} value={bankStmt.pasted} onChange={(e) => setBankStmt({ ...bankStmt, pasted: e.target.value })}
                      placeholder={"2026-02-14, 45000, AFCONS INFRA NEFT, UTRN123\n2026-02-15, 12000, PAHAL ENTERPRISE, UTRN124"}
                      className="w-full border border-slate-300 rounded-sm p-2 text-xs font-mono mt-1" data-testid="bank-paste-textarea" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Or upload statement PDF/CSV</label>
            <div className="mt-1 flex items-center gap-2">
              <label className="flex items-center gap-1 text-xs font-bold px-2 py-2 bg-white border-2 border-slate-300 rounded-sm cursor-pointer hover:bg-slate-50">
                <input type="file" accept="application/pdf,text/csv,.csv,.xlsx" className="hidden" data-testid="bank-file-input"
                       onChange={async (e) => {
                         const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
                         setUploadingBank(true);
                         try {
                           const fd = new FormData(); fd.append("file", f); fd.append("note", "Debtor bank statement");
                           const r = await api.post("/uploads/", fd, { headers: { "Content-Type": "multipart/form-data" } });
                           setForm((s) => ({ ...s, bank_statement_file_id: r.data.file_id, bank_statement_name: r.data.original_name }));
                           toast.success("Statement uploaded — now paste rows and preview");
                         } catch (err) { toast.error(apiError(err)); } finally { setUploadingBank(false); }
                       }} />
                {uploadingBank ? "Uploading…" : (form.bank_statement_file_id ? "Replace" : "Attach")}
              </label>
              {form.bank_statement_file_id && <span className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-300 px-2 py-1 rounded-sm">✓ {form.bank_statement_name}</span>}
            </div>
            <div className="flex gap-2 mt-2">
              <Button size="sm" variant="outline" onClick={previewBank} className="border-slate-300" data-testid="preview-bank-btn">Preview matches</Button>
              {bankStmt.suggestions.length > 0 && <Button size="sm" onClick={commitBank} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="commit-bank-btn">Commit matched</Button>}
            </div>
          </div>
        </div>
        {bankStmt.suggestions.length > 0 && (
          <div className="mt-3 overflow-x-auto">
            <table className="dense-table w-full text-xs">
              <thead><tr><th>Date</th><th>Amount (₹)</th><th>Narration</th><th>Ref</th><th>Suggested debtor</th><th>Outstanding (₹)</th><th>Conf</th></tr></thead>
              <tbody>
                {bankStmt.suggestions.map((s) => (
                  <tr key={`${s.row.date}-${s.row.reference_no || ""}-${s.row.amount}-${s.row.narration || ""}`} className={s.suggested_debtor_id ? "" : "bg-red-50"}>
                    <td>{s.row.date}</td>
                    <td className="num">{fmtINR(s.row.amount)}</td>
                    <td className="max-w-xs truncate">{s.row.narration}</td>
                    <td>{s.row.reference_no}</td>
                    <td className={s.suggested_debtor_id ? "font-bold text-emerald-800" : "italic text-red-700"}>{s.suggested_debtor_name || "— no match —"}</td>
                    <td className="num">{fmtINR(s.outstanding || 0)}</td>
                    <td className="num">{Math.round((s.confidence || 0) * 100)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <h3 className="font-bold text-slate-800 mb-2">Outstanding</h3>
        <table className="dense-table w-full text-sm">
          <thead><tr><th>Debtor</th><th>Credit ₹</th><th>Payments ₹</th><th>Outstanding ₹</th></tr></thead>
          <tbody>
            {ledger.map((l) => (
              <tr key={l.debtor_id}>
                <td className="font-bold">{l.name}</td>
                <td className="num">{fmtINR(l.credit_sales)}</td>
                <td className="num">{fmtINR(l.payments)}</td>
                <td className={"num font-bold " + (l.outstanding > 0 ? "text-red-600" : "text-emerald-700")}>{fmtINR(l.outstanding)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="p-3 border-2 border-slate-300 rounded-sm" data-testid="vehicle-statement-card">
        <div className="flex items-center gap-2 mb-2">
          <Truck size={16} className="text-slate-700" />
          <h3 className="font-bold text-slate-800">Per-Vehicle Statement (B2B invoicing)</h3>
        </div>
        <p className="text-xs text-slate-600 mb-3">Groups every credit sale by vehicle number over a date range. Hand this to Afcons / Bhoir / Pahal so they can validate individual truck fuelings against their internal fleet log.</p>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Debtor</label>
            <select value={stmt.debtor_id} onChange={(e) => setStmt({ ...stmt, debtor_id: e.target.value })} className="w-full h-11 border border-slate-300 rounded-sm px-2 bg-white" data-testid="stmt-debtor-select">
              <option value="">— Select debtor —</option>
              {activeDebtors.map((d) => <option key={d.debtor_id} value={d.debtor_id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600">From</label>
            <Input type="date" value={stmt.from_date} onChange={(e) => setStmt({ ...stmt, from_date: e.target.value })} className="h-11 border border-slate-300" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600">To</label>
            <Input type="date" value={stmt.to_date} onChange={(e) => setStmt({ ...stmt, to_date: e.target.value })} className="h-11 border border-slate-300" />
          </div>
          <Button data-testid="load-stmt-btn" onClick={loadStatement} className="bg-slate-900 hover:bg-slate-800 text-white h-11">Build statement</Button>
          {stmtData && (
            <Button data-testid="print-stmt-btn" onClick={printStatement} variant="outline" className="border-slate-300 h-11"><Printer size={14} className="mr-1" /> Print</Button>
          )}
        </div>

        {stmtData && (
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between bg-slate-100 border border-slate-300 rounded-sm p-3">
              <div>
                <div className="text-xs uppercase text-slate-500 font-bold tracking-wider">Statement for</div>
                <div className="text-lg font-bold text-slate-900">{stmtData.debtor.name}</div>
                <div className="text-xs text-slate-600">{stmtData.from_date} → {stmtData.to_date}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase text-slate-500 font-bold tracking-wider">Grand total</div>
                <div className="text-2xl num font-bold text-slate-900" data-testid="stmt-grand-total">{fmtINR(stmtData.totals.amount)}</div>
                <div className="text-xs text-slate-600">{stmtData.totals.count} txns · {stmtData.totals.qty.toFixed(2)} L/kg</div>
              </div>
            </div>

            {stmtData.vehicles.length === 0 && (
              <div className="text-sm text-slate-600 italic p-3 border border-dashed border-slate-300 rounded-sm">No credit sales for this debtor in this range.</div>
            )}

            {stmtData.vehicles.map((v) => (
              <div key={v.vehicle_no} className="border-2 border-slate-300 rounded-sm bg-white" data-testid={`vehicle-row-${v.vehicle_no.replace(/\s+/g, "-")}`}>
                <button type="button" onClick={() => setExpanded({ ...expanded, [v.vehicle_no]: !expanded[v.vehicle_no] })}
                        className="w-full flex items-center justify-between p-3 hover:bg-slate-50">
                  <div className="flex items-center gap-2">
                    <Truck size={16} className={v.vehicle_no.startsWith("—") ? "text-amber-600" : "text-slate-700"} />
                    <span className={"font-bold " + (v.vehicle_no.startsWith("—") ? "text-amber-800 italic" : "text-slate-900")}>{v.vehicle_no}</span>
                    <span className="text-xs text-slate-500">· {v.count} txns · {v.qty.toFixed(2)} L/kg</span>
                  </div>
                  <div className="num text-lg font-bold text-slate-900">{fmtINR(v.amount)}</div>
                </button>
                {expanded[v.vehicle_no] && (
                  <table className="dense-table w-full text-xs border-t border-slate-200">
                    <thead className="bg-slate-50">
                      <tr><th>Date</th><th>Shift</th><th>Cashier</th><th>Fuel</th><th>Qty</th><th>Amount</th><th>Driver</th><th>Slip #</th></tr>
                    </thead>
                    <tbody>
                      {v.txns.map((t, i) => (
                        <tr key={`${t.date}-${t.shift_code}-${t.slip_no || i}`}>
                          <td>{t.date}</td>
                          <td>{t.shift_code}</td>
                          <td>{t.cashier_name}</td>
                          <td>{t.fuel}</td>
                          <td className="num">{t.qty.toFixed(2)}</td>
                          <td className="num font-bold">{fmtINR(t.amount)}</td>
                          <td>{t.driver_name}</td>
                          <td>{t.slip_no}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
